import { Component, computed, inject, signal, ViewChild } from '@angular/core';
import { SkeletonModule } from 'primeng/skeleton';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../api.service';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { TAGInvItemGroup } from '../../../../models/taginventory';
import { MessageService } from 'primeng/api';
import { Socket } from 'ngx-socket-io';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputIconModule } from 'primeng/inputicon';
import { IconFieldModule } from 'primeng/iconfield';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { getInventoryInstanceByCode } from '../inventoryHelper';
import { EmptyStateComponent } from '../../../components/empty-state/empty-state.component';
import { dialogConfig } from '../../../dialogs';
import {
  BarcodeScannerSearchField,
  CodeScannedEvent,
} from '../../../components/barcode-scanner-search-field/barcode-scanner-search-field';
import { PrinterService } from '../../../printer.service';
import { TooltipModule } from 'primeng/tooltip';
import { MatIcon } from '../../../components/mat-icon/mat-icon';
import { GroupEditDialog, GroupEditDialogData } from '../group-edit-dialog/groupEditDialog';

export interface GroupListDialogData {
  socket: Socket;
}

@Component({
  selector: 'app-internal-group-list-dialog',
  imports: [
    SkeletonModule,
    DialogModule,
    ButtonModule,
    TranslatePipe,
    InputGroupModule,
    InputGroupAddonModule,
    InputIconModule,
    IconFieldModule,
    InputTextModule,
    FormsModule,
    EmptyStateComponent,
    BarcodeScannerSearchField,
    TooltipModule,
    MatIcon,
  ],
  providers: [DialogService, TranslateService],
  templateUrl: './groupListDialog.html',
  styleUrl: './groupListDialog.scss',
})
export class GroupListDialog {
  private dialogService = inject(DialogService);
  private messageService = inject(MessageService);
  private translate = inject(TranslateService);
  public api = inject(ApiService);
  private printer = inject(PrinterService);

  @ViewChild(BarcodeScannerSearchField) search: BarcodeScannerSearchField;

  groups = signal<TAGInvItemGroup[]>([]);
  loadingGroups = signal(true);

  searchFilter = signal('');
  filterItemID = signal('');
  filterInstID = signal('');

  filteredGroups = computed(() => {
    var filtered: TAGInvItemGroup[] = [];

    if (this.filterItemID() == '' && this.filterInstID() == '' && this.searchFilter() == '') {
      filtered = this.groups();
    } else
      for (let group of this.groups()) {
        if (this.filterItemID() != '') {
          if (this.filterInstID() != '') {
            if (group.containsItemInstance(this.filterItemID(), this.filterInstID())) {
              if (filtered.findIndex((t) => t._id == group._id) == -1) filtered.push(group);
            }
          } else {
            if (group.containsItem(this.filterItemID())) {
              if (filtered.findIndex((t) => t._id == group._id) == -1) filtered.push(group);
            }
          }
        } else {
          if (
            group._id.indexOf(this.searchFilter().toLowerCase()) != -1 ||
            group.name.toLowerCase().indexOf(this.searchFilter().toLowerCase()) != -1
          ) {
            if (filtered.findIndex((t) => t._id == group._id) == -1) filtered.push(group);
          }
        }
      }

    return filtered;
  });

  constructor(
    public dialogRef: DynamicDialogRef<GroupListDialog>,
    public config: DynamicDialogConfig<GroupListDialogData>,
  ) {
    config.header = this.translate.translate('internal.inventory.groupList.title')();
  }

  ngOnInit() {
    this.api.waitForAPI().then(() => {
      this.loadGroups();
    });
  }

  loadGroups() {
    this.loadingGroups.set(true);

    this.api
      .getInvItemGroups()
      .then((invGroups: TAGInvItemGroup[]) => {
        this.groups.set(invGroups.filter((g) => !g.deleted));
        this.loadingGroups.set(false);
      })
      .catch((reason) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.translate('errors.couldNotLoadInventoryGroups')(),
          detail: reason.message,
        });
      });
  }

  ngAfterViewInit() {
    this.resetScannerField();
  }

  resetScannerField() {
    this.search?.reset();
    this.filterItemID.set('');
    this.filterInstID.set('');
  }

  codeScanned(code: CodeScannedEvent) {
    getInventoryInstanceByCode(code.code, this.api)
      .then((result) => {
        let item = result.item;
        let instanceID = result.instanceID;

        console.log(result);

        if (item && instanceID) {
          this.searchFilter.set('');
          this.filterItemID.set(item._id);
          this.filterInstID.set(instanceID);
        } else {
          this.searchFilter.set('');
          this.filterItemID.set(item._id);
          this.filterInstID.set('');
        }

        console.log(
          'ItemID: ' +
            this.filterItemID() +
            ' InstID: ' +
            this.filterInstID() +
            ' Text: ' +
            this.searchFilter(),
        );
      })
      .catch((reason) => {
        this.searchFilter.set(code.code);
        this.filterItemID.set('');
        this.filterInstID.set('');

        console.log(
          'ItemID: ' +
            this.filterItemID() +
            ' InstID: ' +
            this.filterInstID() +
            ' Text: ' +
            this.searchFilter(),
        );
      });
  }

  showGroup(group: TAGInvItemGroup) {
    const groupEditRef = this.dialogService.open(
      GroupEditDialog,
      dialogConfig<GroupEditDialogData>({
        closable: true,
        data: {
          group: group,
          socket: this.config.data.socket,
        },
      }),
    );

    groupEditRef.onClose.subscribe((result) => {
      this.resetScannerField();
    });
  }

  newGroup() {
    const groupEditRef = this.dialogService.open(
      GroupEditDialog,
      dialogConfig<GroupEditDialogData>({
        closable: true,
        data: {
          group: new TAGInvItemGroup(),
          socket: this.config.data.socket,
        },
      }),
    );

    groupEditRef.onClose.subscribe((result) => {
      if (result && !result.delete) {
        this.groups.update((g) => [...g, result]);
      } else {
        console.log('Cancelled');
      }

      this.resetScannerField();
    });
  }

  printGroupLabel(group: TAGInvItemGroup) {
    if (!this.printer.isConnected())
      this.printer
        .connectToPrinter()
        .then((result) => {
          this.printer.printGroupLabels([group]);
        })
        .catch((reason) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.translate('errors.couldNotConnectToPrinter')(),
            detail: reason.message,
          });
        });
    else this.printer.printGroupLabels([group]);
  }

  printItemLabels(group: TAGInvItemGroup) {
    if (!this.printer.isConnected())
      this.printer
        .connectToPrinter()
        .then((result) => {
          for (let item of group.items) {
            this.printSingleItemLabel(item.itemid, item.instanceid);
          }
        })
        .catch((reason) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.translate('errors.couldNotConnectToPrinter')(),
            detail: reason.message,
          });
        });
    else
      for (let item of group.items) {
        this.printSingleItemLabel(item.itemid, item.instanceid);
      }
  }

  printSingleItemLabel(itemid: string, instanceid: string) {
    this.api
      .getInvItem(itemid)
      .then((item) => {
        if (!this.printer.isConnected())
          this.printer
            .connectToPrinter()
            .then((result) => {
              this.printer.printItemLabels(
                item,
                item.instances.filter((inst) => inst.itemid == instanceid),
              );
            })
            .catch((reason) => {
              this.messageService.add({
                severity: 'error',
                summary: this.translate.translate('errors.couldNotConnectToPrinter')(),
                detail: reason.message,
              });
            });
        else
          this.printer.printItemLabels(
            item,
            item.instances.filter((inst) => inst.itemid == instanceid),
          );
      })
      .catch((reason) => {
        console.error(reason);
      });
  }
}
